<template>
  <div v-if="icon" :class="classList">
    <input
      :type="type"
      :name="name"
      :placeholder="placeholder"
      @focus="focusIn"
      @focusout="focusOut"
      v-model="content"
      tabindex="0"
      @input="update"
      :disabled="disabled" />
    <div class="a-input__clear" v-if="content && clearButton === true" @click="clear()">
      <XMarkIcon />
    </div>
    <button
      class="a-input__icon"
      :class="{ 'a-input__icon--disabled': buttonDisabled }"
      @click="focusIn"
      :disabled="buttonDisabled">
      <slot />
    </button>
  </div>
  <div :class="classList" v-else-if="type === 'text' || type === 'hidden' || type === 'password'">
    <input
      :type="type"
      :name="name"
      :maxlength="maxlength"
      :placeholder="placeholder"
      @focus="focusIn"
      @focusout="focusOut"
      tabindex="0"
      v-model="content"
      @input="update"
      :disabled="disabled"
      :autocomplete="autocomplete" />
  </div>
  <div :class="classList" v-else-if="type === 'date'">
    <input
      type="date"
      :name="name"
      :placeholder="placeholder"
      @focus="focusIn"
      @focusout="focusOut"
      tabindex="0"
      v-model="content"
      @input="update"
      :min="min"
      :max="max"
      :disabled="disabled" />
  </div>
  <div :class="classList"  v-else-if="type === 'textarea'">
    <textarea
      :type="type"
      :name="name"
      :style="`min-height: ${minHeight}px`"
      :placeholder="placeholder"
      @focus="focusIn"
      @focusout="focusOut"
      tabindex="0"
      v-model="content"
      @input="update"
      :disabled="disabled"></textarea>
  </div>
</template>

<script>
import { XMarkIcon } from '@/components/icons/fontIcons';

export default {
  name: 'VInput',
  introduction: 'Styled text input or datepicker',
  components: {
    XMarkIcon,
  },
  props: {
    type: {
      type: String,
      required: true,
      note: 'Selects the element to render ("text", "hidden", "password", "textarea" or "date").',
    },
    icon: {
      type: Boolean,
      default: false,
      note: 'Display an icon button at the end of the text box.',
    },
    disabled: {
      type: Boolean,
      default: false,
      note: 'Toggles disabling the element.',
    },
    buttonDisabled: {
      type: Boolean,
      default: false,
      note: 'Disables the icon button.',
    },
    fluid: {
      type: Boolean,
      default: true,
      note: 'Sets width to 100%.',
    },
    clearButton: {
      type: Boolean,
      default: false,
      note: 'Adds a clear icon to the end of the text box.',
    },
    minHeight: {
      type: Number,
      default: 75,
      note: 'Sets the text area min height.',
    },
    subtype: {
      type: String,
      note: 'Not used.',
    },
    size: {
      type: String,
      note: 'Define the size of the element (can be "small")',
    },
    placeholder: {
      type: String,
      note: 'Set the placeholder text of the input.',
    },
    name: {
      type: String,
      note: 'Set the name field of the input.',
    },
    min: {
      type: String,
      note: 'Set the min date on the date picker.',
    },
    max: {
      type: String,
      note: 'Set the max date on the date picker.',
    },
    value: {
      type: String,
      default: '',
      note: 'Sets the initial value of the input.',
    },
    autocomplete: {
      type: String,
      note: 'Sets the autocomplete mode of the input',
    },
    maxlength: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      focus: false,
      hasUpdated: false,
    };
  },
  computed: {
    classList() {
      const {
        fluid, focus, type, icon, subtype, size,
      } = this;
      return {
        'a-input': true,
        'a-input--fluid': fluid,
        'a-input--focus': focus,
        'a-input--icon': icon,
        [`a-input--${type}`]: type,
        [`a-input--${subtype}`]: subtype,
        [`a-input--${size}`]: size,
      };
    },
    content: {
      get() { return this.value; },
      set(value) { this.$emit('input', value); },
    },
  },
  methods: {
    focusIn() {
      this.focus = true;
    },
    focusOut() {
      this.focus = false;
      if (this.hasUpdated) {
        this.$emit('updated', this.content);
        this.hasUpdated = false;
      }
    },
    update() {
      this.hasUpdated = true;
    },
    clear() {
      this.content = '';
      this.$emit('updated', this.content);
    },
  },
};
</script>

<style scoped>
.a-input {
  background: var(--signable-secondary-4-shade-3);
  background: white;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  float: left;
  transition: border .25s ease, box-shadow .25s ease;
  overflow: hidden;
}

.a-input input, .a-input textarea {
  padding: var(--spacing-small);
  border: 0;
  background: transparent;
  outline: none;
  font-size: inherit;
  color: inherit;
  font-family: inherit;
  line-height: 1.45;
}

.a-input textarea {
  display: block;
  min-width: 100%;
  max-width: 100%;
}

.a-input--textarea {
  width: 100%;
}

.a-input--fluid {
  width: 100%;
}

.a-input--fluid input {
  width: 100%;
}

.a-input--small input {
  height: 36px;
  line-height: 36px;
}

.a-input--small button {
  height: 38px;
  line-height: 0;
}

.a-input--icon {
  position: relative;
}

.a-input--icon input {
  width: 100%;
  padding-right: calc(45px + var(--spacing-small)); /** icon width */
}

.a-input--icon .a-input__icon {
  position: absolute;
  right: 0;
  top: 0;
  line-height: 0;
  border: 1px solid var(--color-border);
  padding: 12px;
  margin: -1px -1px -1px 0px;
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
  transition: border .25s ease, color .25s ease;
}

.a-input--icon .a-input__icon input {
  float: left;
}

.a-input--icon .a-input__icon svg {
  height: 13px;
  width: 13px;
}

.a-input--icon .a-input__icon--disabled {
  background: var(--signable-secondary-4-shade-3);
  color: var(--signable-secondary-4);
}

.a-input--icon .a-input__icon--disabled:hover, .a-input--icon .a-input__icon--disabled:active {
  cursor: not-allowed;
  background: var(--signable-secondary-4-shade-3);
  color: var(--signable-secondary-4-shade-1);
}

.a-input--icon .a-input__clear {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 60px;
  cursor: pointer;
  color: var(--signable-secondary-3-shade-1);
  transition: color .25s ease;
}

.a-input--icon .a-input__clear:hover {
  color: var(--signable-secondary-3);
}

.a-input--icon .a-input__clear svg {
  vertical-align: text-top;
}

.a-input--focus {
  border-color: var(--signable-secondary-2-shade-3);
  border-width: 1px;
  box-shadow: 0px 0px 0px 2px rgba(141, 173, 192, 0.25);
  transition: border .25s ease, box-shadow .25s ease, color .25s ease;
  position: relative;
}

.a-input--focus .a-input__icon {
  border-color: var(--signable-secondary-2-shade-3);
  transition: border .25s ease, color .25s ease;
}

.a-input--focus .a-input__icon svg {
  color: var(--signable-primary-1);
  transition: color .25s ease;
}

.a-input--focus .a-input__icon--disabled {
  background: var(--signable-secondary-4-shade-3);
  color: var(--signable-secondary-4);
}

.a-input--focus .a-input__icon--disabled:hover, .a-input--focus .a-input__icon--disabled:active {
  cursor: not-allowed;
  background: var(--signable-secondary-4-shade-3);
  color: var(--signable-secondary-4-shade-1);
}

.a-input--error {
  border-color: var(--color-error);
}

.a-input--error .a-input__icon {
  border-color: var(--color-error);
  transition: border .25s ease, color .25s ease;
}

.a-input--error .a-input__icon svg {
  color: var(--color-error);
  transition: color .25s ease;
}

.a-input--error.a-input--focus {
  box-shadow: 0px 0px 0px 2px rgba(231, 76, 60, 0.25);
}
</style>
